import React, { useMemo, useRef, useState } from "react";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import PropTypes from "prop-types";
import Checkbox from "../../inputs/checkbox";
import { cssToken } from "../../../utilities/cssToken";
import { scorePercent } from "./score";
import { fractionalIndex } from "../../../utilities/fractionalIndex";

// CSS
import "./index.scss";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

/**
 * The selected score marked on the curve (Option B, ASB-2995): the band above it shaded, a
 * dashed line at it, and a faint solid line at the score under the pointer. Chart.js draws on a canvas, so the colours come in as resolved token values.
 */
export const scoreMarkerPlugin = {
	id: "scoreMarker",
	beforeDraw(chart, _, opts) {
		const x = xAt(chart, opts.index);
		if (x == null || !opts.shade) return;
		const { top, bottom, right } = chart.chartArea;
		const { ctx } = chart;
		ctx.save();
		ctx.fillStyle = opts.bandColor;
		ctx.fillRect(x, top, right - x, bottom - top);
		ctx.restore();
	},
	afterDatasetsDraw(chart, _, opts) {
		const hovered = chart.getActiveElements?.()[0]?.index;
		if (hovered != null && hovered !== opts.index) {
			vline(chart, xAt(chart, hovered), opts.hoverColor, []);
		}
		const x = xAt(chart, opts.index);
		// `extend`: on past the plot to the canvas's bottom edge, where a control drawn
		// directly below (the edit-mode slider) picks the line up.
		if (x != null) vline(chart, x, opts.lineColor, [4, 4], opts.extend ? chart.height : null);
		if (x != null) dotsAt(chart, opts.index, opts.radii ?? []);
		// Where the marker was drawn, for the e2e geometry checks (as data-tick-x below).
		chart.canvas.dataset.markerX = x == null ? "" : String(Math.round(x));
	},
	// The pointer only over the plot, where a click picks a score (see indexAtEvent). Here,
	// not in options.onHover: Chart.js calls that only inside the plot, so it never resets.
	afterEvent(chart, args, opts) {
		chart.canvas.style.cursor = opts.selectable && args.inChartArea ? "pointer" : "default";
	},
	// Where the first and last score sit on the canvas, for a control below the chart to
	// line its own ends up with (the edit-mode slider). Reported only when it changes, since
	// the receiver re-renders.
	afterLayout(chart, _, opts) {
		const scale = chart.scales.x;
		const n = chart.data.labels?.length ?? 0;
		if (!scale || n < 2) return;
		// Each score's x on the canvas, from the chart's own scale: what the e2e geometry
		// check measures the slider's marks against.
		chart.canvas.dataset.tickX = Array.from({ length: n }, (_, i) =>
			Math.round(scale.getPixelForValue(i))
		).join(",");
		if (!opts.onPlotArea) return;
		const left = Math.round(scale.getPixelForValue(0));
		const right = Math.round(chart.width - scale.getPixelForValue(n - 1));
		const last = chart.$plotArea;
		if (last && last.left === left && last.right === right) return;
		chart.$plotArea = { left, right };
		opts.onPlotArea({ left, right });
	},
};

// Off a data point, the curve's height at a fractional index, read straight between its
// neighbours: where the marker's dots go for a score the model has no row for.
function dotsAt(chart, pos, radii) {
	if (pos < 0 || Number.isInteger(pos)) return;
	const lo = Math.floor(pos);
	const t = pos - lo;
	const x = chart.scales.x.getPixelForValue(pos);
	const { ctx } = chart;
	chart.data.datasets.forEach((ds, d) => {
		const a = Number(ds.data[lo]) || 0;
		const b = Number(ds.data[lo + 1]) || 0;
		const y = chart.scales.y.getPixelForValue(a + (b - a) * t);
		ctx.save();
		ctx.fillStyle = ds.borderColor;
		ctx.beginPath();
		ctx.arc(x, y, radii[d] ?? 4, 0, Math.PI * 2);
		ctx.fill();
		ctx.restore();
	});
}

function xAt(chart, index) {
	if (index == null || index < 0) return null;
	return chart.scales.x?.getPixelForValue(index);
}

function vline(chart, x, color, dash, to) {
	if (x == null) return;
	const { top } = chart.chartArea;
	const bottom = to ?? chart.chartArea.bottom;
	const { ctx } = chart;
	ctx.save();
	ctx.strokeStyle = color;
	ctx.lineWidth = 1;
	ctx.setLineDash(dash);
	ctx.beginPath();
	ctx.moveTo(x, top);
	ctx.lineTo(x, bottom);
	ctx.stroke();
	ctx.restore();
}

/**
 * The score index nearest a click, or null when the click is outside the plot (an axis,
 * its title, the gutter): those are not a choice of score.
 */
export function indexAtEvent(chart, evt, n) {
	const { left, right, top, bottom } = chart.chartArea;
	if (evt.x < left || evt.x > right || evt.y < top || evt.y > bottom) return null;
	const raw = chart.scales.x.getValueForPixel(evt.x);
	return Math.min(n - 1, Math.max(0, Math.round(raw)));
}

const prefersReducedMotion = () => {
	try {
		return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	} catch (e) {
		return false;
	}
};

const SERIES = [
	{ key: "users", label: "Unique Users", field: "unique_users_last_30_days", radius: 5 },
	{ key: "views", label: "Page Views", field: "page_views_last_30_days", radius: 4 },
];

/**
 * Reach at each propensity score, from the last model run. `selectedScore` (a whole
 * percent) is marked on the curve, with the band above it shaded. `summary`
 * sits at the start of the legend row (view mode puts the score there).
 *
 * Hovering shows both values at a score. With `onSelectScore` a click on the chart picks a score (edit mode). The y axis
 * scales to the data and draws no values unless `showYAxisValues`, or the viewer turns
 * them on with the `valuesToggle` checkbox. `xAxisLabels={false}` leaves the score labels to a
 * control below that draws its own (the edit-mode slider) and runs the marker down to it. The lines draw in once, on first load.
 */
function PropensityThrottle({
	data,
	status,
	selectedScore,
	mode = "edit",
	summary,
	showYAxisValues = false,
	valuesToggle = false,
	xAxisLabels = true,
	onSelectScore,
	onPlotArea,
}) {
	const [showValues, setShowValues] = useState(showYAxisValues);
	const [hover, setHover] = useState(null);
	const onPlotAreaRef = useRef(onPlotArea);
	onPlotAreaRef.current = onPlotArea;
	const onSelectRef = useRef(onSelectScore);
	onSelectRef.current = onSelectScore;
	const canSelect = typeof onSelectScore === "function";

	const selectedIndex =
		selectedScore == null ? -1 : data.findIndex((row) => scorePercent(row.score) === selectedScore);
	// The marker's position: the score's own index, or between two for a score the curve
	// has no row for, so the line and band still sit where the saved score belongs.
	const markerPos =
		selectedIndex >= 0
			? selectedIndex
			: fractionalIndex(data.map((row) => scorePercent(row.score)), selectedScore);
	const hasChart = status === "finished" && data.length > 0;

	const colors = useMemo(
		() => ({
			users: cssToken("--as-color-primary"),
			views: cssToken("--as-color-success"),
			band: cssToken("--as-color-pale-secondary"),
			grid: cssToken("--as-color-neutral-200"),
			axis: cssToken("--as-color-neutral-300"),
			hover: cssToken("--as-color-neutral-400"),
			text: cssToken("--as-color-text"),
			muted: cssToken("--as-color-neutral-600"),
		}),
		[]
	);
	// The page's font, not Chart.js's Helvetica default; set per chart, never globally,
	// so the dashboard charts keep theirs.
	const family = useMemo(() => cssToken("--as-font-body"), []);

	// Memoised, so a parent re-render (the slider lining itself up, the tooltip moving)
	// does not hand Chart.js new objects -- that would update the chart and cut the
	// first-load draw-in short.
	const graphData = useMemo(
		() => ({
			labels: data.map((row) => `${scorePercent(row.score)}%`),
			datasets: SERIES.map((s) => ({
				label: s.label,
				data: data.map((row) => Number(row[s.field]) || 0),
				borderColor: colors[s.key],
				backgroundColor: colors[s.key],
				borderWidth: 2,
				// A point only at the selected score, as the mockups draw it; any score
				// under the pointer gets one too.
				pointRadius: data.map((_, i) => (i === markerPos ? s.radius : 0)),
				pointHoverRadius: s.radius + 1,
				pointHoverBorderWidth: 2,
				pointHoverBorderColor: cssToken("--as-color-white"),
			})),
		}),
		[data, markerPos, colors]
	);

	const options = useMemo(
		() => ({
			responsive: true,
			// Draw in once; selections and toggles redraw instantly (updateMode="none").
			animation: prefersReducedMotion() ? false : { duration: 500, easing: "easeOutQuart" },
			interaction: { mode: "index", intersect: false },
			// With x labels, the last label's overhang pads the right edge. Without them the
			// last score would sit on the canvas edge, where a control below cannot reach it:
			// a slider puts its end mark half a thumb inside its own box.
			layout: { padding: { right: xAxisLabels ? 0 : 12 } },
			onClick: (evt, _, chart) => {
				if (!onSelectRef.current || data.length === 0) return;
				const i = indexAtEvent(chart, evt, data.length);
				if (i != null) onSelectRef.current(scorePercent(data[i].score));
			},
			plugins: {
				legend: { display: false },
				// Drawn as an HTML card below (setHover); the canvas one cannot take tokens.
				tooltip: {
					enabled: false,
					external: ({ chart, tooltip }) => {
						const index = tooltip.opacity === 0 ? null : tooltip.dataPoints?.[0]?.dataIndex;
						setHover((h) => {
							if (index == null) return null;
							const x = Math.round(chart.scales.x.getPixelForValue(index));
							if (h && h.index === index && h.x === x) return h;
							return { index, x, flip: x > chart.width * 0.6, top: chart.chartArea.top };
						});
					},
				},
				scoreMarker: {
					index: markerPos,
					radii: SERIES.map((s) => s.radius),
					// Both modes (Jose, 2026-10-01): it shows what the selection includes.
					shade: true,
					extend: !xAxisLabels,
					lineColor: colors.users,
					bandColor: colors.band,
					hoverColor: colors.hover,
					selectable: canSelect,
					onPlotArea: (area) => onPlotAreaRef.current?.(area),
				},
			},
			scales: {
				x: {
					// No edge offset: the first score sits on the y axis and the last on the
					// right edge, the same span the slider's track covers.
					offset: false,
					grid: { display: false },
					border: { display: true, color: colors.axis },
					ticks: {
						display: xAxisLabels,
						font: { family, size: 12, weight: "bold" },
						color: (ctx) => (ctx.index === selectedIndex ? colors.users : colors.text),
					},
					title: {
						display: xAxisLabels,
						text: "Propensity",
						color: colors.text,
						font: { family, size: 13, weight: "bold" },
					},
				},
				y: {
					// Elastic: the axis follows the data with headroom, never a fixed range.
					beginAtZero: true,
					grace: "10%",
					grid: { color: colors.grid, drawTicks: false },
					border: { display: true, color: colors.axis },
					ticks: {
						display: showValues,
						maxTicksLimit: 5,
						padding: 6,
						font: { family, size: 12 },
						color: colors.muted,
						callback: (value) => compact.format(value),
					},
					title: {
						display: true,
						text: "Reach",
						color: colors.text,
						font: { family, size: 13, weight: "bold" },
					},
				},
			},
		}),
		[data, selectedIndex, markerPos, showValues, xAxisLabels, colors, family, canSelect]
	);

	const hoverRow = hover ? data[hover.index] : null;
	// The same card in both modes: the score and its two values, nothing restated.

	return (
		<div className={`propensity-throttle propensity-throttle--${mode}${hasChart ? "" : " propensity-throttle--empty"}`}>
			{(summary || hasChart) && (
				<div className="propensity-throttle__head">
					{summary}
					{hasChart && (
						<ul className="propensity-throttle__legend">
							{SERIES.map((s) => (
								<li
									key={s.key}
									className="propensity-throttle__legend-item"
								>
									<span
										className={`propensity-throttle__swatch propensity-throttle__swatch--${s.key}`}
										aria-hidden="true"
									/>
									{s.label}
								</li>
							))}
						</ul>
					)}
				</div>
			)}
			{hasChart && valuesToggle && (
				// Above the y axis, in the same place in both modes: it governs that axis.
				<div className="propensity-throttle__axis-control">
					<Checkbox
						name={`propensity-axis-values-${mode}`}
						options={[{ value: "on", label: "Show reach axis values" }]}
						selected={showValues ? [{ value: "on" }] : []}
						onInput={(e) => setShowValues(e.target.checked)}
					/>
				</div>
			)}
			{hasChart ? (
				<div
					className={[
						"propensity-throttle__chart",
						canSelect ? "propensity-throttle__chart--selectable" : "",
					]
						.join(" ")
						.trim()}
					onMouseLeave={() => setHover(null)}
				>
					<Line
						options={options}
						data={graphData}
						plugins={[scoreMarkerPlugin]}
						updateMode="none"
						aria-label="Reach at each propensity score"
						role="img"
					/>
					{hoverRow && (
						<div
							className={[
								"propensity-throttle__tooltip",
								hover.flip ? "propensity-throttle__tooltip--flip" : "",
							]
								.join(" ")
								.trim()}
							style={{ left: hover.x, top: hover.top }}
							// Follows the pointer; announcing it on every move would be noise.
							aria-hidden="true"
						>
							<p className="propensity-throttle__tooltip-title">
								{scorePercent(hoverRow.score)}+
								{hover.index === selectedIndex ? (
									<span className="propensity-throttle__tooltip-tag">
										Current
									</span>
								) : null}
							</p>
							{SERIES.map((s) => (
								<p
									key={s.key}
									className="propensity-throttle__tooltip-row"
								>
									<span
										className={`propensity-throttle__swatch propensity-throttle__swatch--${s.key}`}
										aria-hidden="true"
									/>
									<span className="propensity-throttle__tooltip-label">{s.label}</span>
									<span className="propensity-throttle__tooltip-value">
										{(Number(hoverRow[s.field]) || 0).toLocaleString()}
									</span>
								</p>
							))}
							{canSelect && hover.index !== selectedIndex ? (
								<p className="propensity-throttle__tooltip-note">Click to select</p>
							) : null}
						</div>
					)}
				</div>
			) : (
				<p className="p4 propensity-throttle__empty">
					{status === "created" || status === "started"
						? "Data not yet available. Please check back later."
						: "Data not available"}
				</p>
			)}
		</div>
	);
}

PropensityThrottle.propTypes = {
	data: PropTypes.array.isRequired,
	status: PropTypes.string.isRequired,
	/** whole percent (70), marked on the curve */
	selectedScore: PropTypes.number,
	/** "view" (read-only) or "edit" (with onSelectScore, the chart is clickable) */
	mode: PropTypes.oneOf(["view", "edit"]),
	/** rendered at the start of the legend row */
	summary: PropTypes.node,
	/** start with compact y values (1.2M) drawn; off by default, as in the mockups */
	showYAxisValues: PropTypes.bool,
	/** offer the viewer a "Show reach axis values" checkbox above the y axis */
	valuesToggle: PropTypes.bool,
	/** false: no x tick labels or title, and the marker runs to the canvas's bottom edge */
	xAxisLabels: PropTypes.bool,
	/** (score: whole percent) => void; makes the chart clickable */
	onSelectScore: PropTypes.func,
	/** ({ left, right }) => void: px from the canvas edges to the first and last score */
	onPlotArea: PropTypes.func,
};

export default PropensityThrottle;
